import { useState } from "react";
import { ShareNetwork } from "@phosphor-icons/react";
import { generateShareText, copyShareText, type ShareData } from "../lib/shareCard";
import { trackEvent } from "../lib/clarityEvents";
import { useLocale } from "../lib/i18n";

function WhatsAppIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

interface Props {
  data: ShareData;
  streak: number;
  displayName?: string;
}

/** Share game results — WhatsApp for Hebrew, native share/clipboard for others */
export function ShareButton({ data, streak, displayName }: Props) {
  const { t, locale } = useLocale();
  const [status, setStatus] = useState<"idle" | "copied">("idle");
  const isHebrew = locale === "he";

  const handleShare = async () => {
    trackEvent("share");
    const text = generateShareText(data, streak, displayName, locale === "he" ? "he" : "en");

    if (isHebrew) {
      // WhatsApp for Hebrew/Israel users
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
      return;
    }

    // Native share or clipboard for other locales
    if (navigator.share) {
      try { await navigator.share({ text }); return; } catch { /* cancelled */ }
    }
    const ok = await copyShareText(text);
    if (ok) { setStatus("copied"); setTimeout(() => setStatus("idle"), 2000); }
  };

  if (isHebrew) {
    return (
      <button
        onClick={handleShare}
        className="w-full py-4 rounded-2xl flex items-center justify-center gap-3 font-bold text-lg bg-[#25D366] hover:bg-[#20BD5A] text-white transition-colors shadow-[0_4px_20px_rgba(37,211,102,0.3)]"
      >
        <WhatsAppIcon size={24} />
        {t("share.whatsapp")}
      </button>
    );
  }

  return (
    <button
      onClick={handleShare}
      className="btn-primary w-full py-4 rounded-2xl flex items-center justify-center gap-3 font-bold text-lg"
    >
      <ShareNetwork size={22} weight="fill" />
      {status === "copied" ? t("share.copied") : t("share.result")}
    </button>
  );
}

/** Invite button — WhatsApp for Hebrew, native share for others */
export function WhatsAppInviteButton() {
  const { t, locale } = useLocale();
  const isHebrew = locale === "he";

  const handleInvite = () => {
    trackEvent("whatsapp_invite");
    const text = t("share.inviteText");

    if (isHebrew) {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
      return;
    }

    if (navigator.share) {
      navigator.share({ text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  if (isHebrew) {
    return (
      <button
        onClick={handleInvite}
        className="w-full py-3.5 rounded-2xl flex items-center justify-center gap-3 font-bold text-base bg-[#25D366] hover:bg-[#20BD5A] text-white transition-colors shadow-[0_4px_20px_rgba(37,211,102,0.3)]"
      >
        <WhatsAppIcon size={22} />
        {t("share.invite")}
      </button>
    );
  }

  return (
    <button
      onClick={handleInvite}
      className="btn-primary w-full py-3.5 rounded-2xl flex items-center justify-center gap-3 font-bold text-base"
    >
      <ShareNetwork size={20} weight="fill" />
      {t("share.invite")}
    </button>
  );
}
